import React, { useEffect, useRef } from'react';
import { gsap } from'../../animations/gsap.config';
import { ScrollTrigger } from'../../animations/gsap.config';
import aboutData from'../../data/about.json';
import { ArrowDown } from'lucide-react';



/* ─────────────────────────────────────────────
 FLOATING STAT PILL
───────────────────────────────────────────── */
const StatPill = ({ stat, index }) => {
 const ref = useRef(null);

 useEffect(() => {
 gsap.to(ref.current, {
 y: index % 2 === 0 ? -10 : 10,
 duration: 2.5 + index * 0.5,
 ease:'sine.inOut',
 repeat: -1,
 yoyo: true,
 delay: index * 0.4,
 });
 }, [index]);

 return (
 <div
 ref={ref}
 className="glass rounded-2xl px-5 py-3 border border-white/10
 hover:border-primary-500/40 transition-colors duration-300 cursor-default"
 >
 <p className="text-secondary-400 font-black text-xl font-mono leading-none">
 {stat.value}
 </p>
 <p className="text-[10px] text-slate-400 mt-1 tracking-wide">{stat.label}</p>
 </div>
 );
};

/* ─────────────────────────────────────────────
 SCROLL INDICATOR
───────────────────────────────────────────── */
const ScrollIndicator = () => {
 const lineRef = useRef(null);

 useEffect(() => {
 gsap.to(lineRef.current, {
 scaleY: 0,
 transformOrigin:'top center',
 duration: 0.8,
 ease:'power2.inOut',
 repeat: -1,
 yoyo: true,
 delay: 0.5,
 });
 }, []);

 return (
 <div className="flex flex-col items-center gap-2">
 <span className="text-[10px] font-medium tracking-[0.3em] uppercase text-slate-500">
 Discover Our Story
 </span>
 <div
 ref={lineRef}
 className="w-px h-10 bg-gradient-to-b from-primary-400 to-transparent"
 />
 <ArrowDown size={12} className="text-slate-500 animate-bounce" />
 </div>
 );
};

/* ─────────────────────────────────────────────
 MAIN ABOUT HERO
───────────────────────────────────────────── */
const AboutHero = () => {
 const { hero } = aboutData;

 const sectionRef = useRef(null);
 const imgRef = useRef(null);
 const overlayRef = useRef(null);
 const meshRef = useRef(null);
 const headLine1Ref = useRef(null);
 const headLine2Ref = useRef(null);
 const descRef = useRef(null);
 const statsRef = useRef(null);
 const scrollCueRef = useRef(null);
 const cornerTLRef = useRef(null);
 const cornerBRRef = useRef(null);
 const vertLineRef = useRef(null);

 /* ── Entry animation ── */
 useEffect(() => {
 const ctx = gsap.context(() => {
 const tl = gsap.timeline({ delay: 0.3 });

 /* Image reveal (clip-path wipe) */
 tl.fromTo(
 imgRef.current,
 { scale: 1.15 },
 { scale: 1.03, duration: 2, ease:'power2.out' },
 );

 /* Overlay fade */
 tl.fromTo(
 overlayRef.current,
 { opacity: 0 },
 { opacity: 1, duration: 1.2, ease:'power2.out' },
 0,
 );

 /* Badge - removed */

 /* Headline line 1 */
 tl.fromTo(
 headLine1Ref.current,
 { y: 80, opacity: 0, skewY: 4 },
 { y: 0, opacity: 1, skewY: 0, duration: 0.9, ease:'power4.out' },
 0.4,
 );

 /* Headline line 2 */
 tl.fromTo(
 headLine2Ref.current,
 { y: 80, opacity: 0, skewY: 4 },
 { y: 0, opacity: 1, skewY: 0, duration: 0.9, ease:'power4.out' },
 0.55,
 );

 /* Description */
 tl.fromTo(
 descRef.current,
 { y: 30, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.7, ease:'power3.out' },
 0.8,
 );

 /* Stats */
 tl.fromTo(
 statsRef.current?.children ?? [],
 { y: 25, opacity: 0, scale: 0.9 },
 {
 y: 0, opacity: 1, scale: 1,
 stagger: 0.12,
 duration: 0.6,
 ease:'back.out(1.4)',
 },
 1.0,
 );

 /* Scroll cue */
 tl.fromTo(
 scrollCueRef.current,
 { y: 20, opacity: 0 },
 { y: 0, opacity: 1, duration: 0.5, ease:'power3.out' },
 1.3,
 );

 /* Corner decorations */
 tl.fromTo(
 [cornerTLRef.current, cornerBRRef.current],
 { opacity: 0, scale: 0.7 },
 {
 opacity: 1, scale: 1,
 stagger: 0.15,
 duration: 0.5,
 ease:'back.out(1.5)',
 },
 0.9,
 );

 /* Vertical line */
 tl.fromTo(
 vertLineRef.current,
 { scaleY: 0 },
 { scaleY: 1, duration: 0.8, ease:'power3.out', transformOrigin:'top' },
 0.7,
 );

 /* Mesh float */
 gsap.to(meshRef.current, {
 y: -20,
 x: 15,
 duration: 6,
 ease:'sine.inOut',
 repeat: -1,
 yoyo: true,
 });
 }, sectionRef);

 return () => ctx.revert();
 }, []);

 /* ── Scroll parallax ── */
 useEffect(() => {
 const ctx = gsap.context(() => {
 ScrollTrigger.create({
 trigger: sectionRef.current,
 start:'top top',
 end:'bottom top',
 scrub: true,
 onUpdate: (self) => {
 const p = self.progress;
 gsap.set(imgRef.current, { y: p * 80, scale: 1.03 + p * 0.05 });
 gsap.set(meshRef.current, { y: p * 60 });
 },
 });
 }, sectionRef);

 return () => ctx.revert();
 }, []);

 return (
 <section
 ref={sectionRef}
 id="about-hero"
 className="relative w-full overflow-hidden"
 style={{ height:'85vh', minHeight:'550px', maxHeight:'800px' }}
 >
 {/* ── BACKGROUND ── */}

 {/* Base gradient */}
 <div
 className="absolute inset-0 z-0"
 style={{
 background:'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #eff6ff 100%)',
 }}
 />

 {/* Background image */}
 <div className="absolute inset-0 z-[1] overflow-hidden">
 <img
 ref={imgRef}
 src={hero.image}
 alt="About Agora Consulting"
 className="w-full h-full object-cover will-change-transform"
 style={{ transform:'scale(1.15)' }}
 />
 </div>

 {/* Multi-layer overlay - updated to dark */}
 <div
 ref={overlayRef}
 className="absolute inset-0 z-[2]"
 style={{
 background:`
 linear-gradient(to right, rgba(6,13,36,0.96) 0%, rgba(6,13,36,0.7) 45%, rgba(6,13,36,0.3) 100%),
 linear-gradient(to top, rgba(6,13,36,0.96) 0%, transparent 40%),
 linear-gradient(to bottom, rgba(6,13,36,0.5) 0%, transparent 30%)
`,
 opacity: 0,
 }}
 />

 {/* Animated mesh */}
 <div
 ref={meshRef}
 className="absolute inset-0 z-[3] pointer-events-none will-change-transform"
 style={{
 background:`
 radial-gradient(ellipse 60% 50% at 30% 60%, rgba(29,78,216,0.2) 0%, transparent 55%),
 radial-gradient(ellipse 40% 40% at 70% 30%, rgba(245,158,11,0.1) 0%, transparent 50%)
`,
 }}
 />

 {/* Dot pattern + noise */}
 <div className="absolute inset-0 z-[4] dot-pattern opacity-15 pointer-events-none" />

 {/* ── CONTENT ── */}
 <div
 className="absolute inset-0 z-20 flex flex-col justify-center
 px-6 sm:px-12 lg:px-20 xl:px-28"
 >
 <div className="max-w-2xl xl:max-w-3xl space-y-6 pt-24 lg:pt-16">

 {/* Headlines */}
 <div className="space-y-2 overflow-hidden">
 <div className="overflow-hidden">
 <h1
 ref={headLine1Ref}
 className="font-display font-black leading-none text-white"
 style={{
 fontSize:'clamp(2.5rem, 6vw, 5rem)',
 letterSpacing:'-0.02em',
 opacity: 0,
 }}
 >
 {hero.headline}
 </h1>
 </div>
 <div className="overflow-hidden">
 <h1
 ref={headLine2Ref}
 className="font-display font-black leading-none text-gradient-primary"
 style={{
 fontSize:'clamp(2.5rem, 6vw, 5rem)',
 letterSpacing:'-0.02em',
 opacity: 0,
 }}
 >
 {hero.headlineAccent}
 </h1>
 </div>
 </div>

 {/* Description */}
 <p
 ref={descRef}
 className="text-slate-300 leading-relaxed max-w-lg"
 style={{
 fontSize:'clamp(0.95rem, 1.4vw, 1.1rem)',
 opacity: 0,
 }}
 >
 {hero.description}
 </p>

 {/* Stats row */}
 <div ref={statsRef} className="flex items-center gap-4 flex-wrap pt-2">
 {hero.stats.map((stat, i) => (
 <StatPill key={i} stat={stat} index={i} />
 ))}
 </div>
 </div>
 </div>

 {/* ── SCROLL CUE ── */}
 <div
 ref={scrollCueRef}
 className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30"
 style={{ opacity: 0 }}
 >
 <ScrollIndicator />
 </div>

 {/* ── DECORATIVE ELEMENTS ── */}

 {/* Top-left corner */}
 <div
 ref={cornerTLRef}
 className="absolute top-20 left-6 sm:left-12 z-20 hidden sm:block"
 style={{ opacity: 0 }}
 >
 <div className="w-14 h-14 border-l-2 border-t-2 border-primary-600/30 rounded-tl-xl" />
 </div>

 {/* Bottom-right corner */}
 <div
 ref={cornerBRRef}
 className="absolute bottom-20 right-6 sm:right-12 z-20 hidden sm:block"
 style={{ opacity: 0 }}
 >
 <div className="w-14 h-14 border-r-2 border-b-2 border-secondary-400/30 rounded-br-xl" />
 </div>

 {/* Vertical accent line */}
 <div
 ref={vertLineRef}
 className="absolute left-0 top-1/4 bottom-1/4 w-[2px] z-20 hidden lg:block"
 style={{
 background:'linear-gradient(to bottom, transparent, rgba(29,78,216,0.4), transparent)',
 transform:'scaleY(0)',
 transformOrigin:'top',
 }}
 />

 {/* Right section number */}
 <div
 className="absolute right-6 top-1/3 hidden xl:flex
 flex-col items-center gap-2 pointer-events-none z-20"
 >
 <p
 className="font-mono text-[10px] tracking-widest text-slate-600"
 style={{ writingMode:'vertical-lr' }}
 >
 ABOUT / WHO WE ARE
 </p>
 </div>

 {/* Bottom gradient fade */}
 <div
 className="absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none"
 style={{
 background:'linear-gradient(to top, #060d24 0%, transparent 100%)',
 }}
 />
 </section>
 );
};

export default AboutHero;
